// Enhanced API utility functions with better error handling
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(
  /\/+$/,
  ""
);

let authToken = null;

export const setToken = (token) => {
  authToken = token;
};

export const removeToken = () => {
  authToken = null;
};

const getHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
  };

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  return headers;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    let errorMessage = "Request failed";
    let errorData = {};

    try {
      errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;

      if (response.status === 401 && !errorData.locked) {
        removeToken();
        localStorage.removeItem("token");
      } else if (response.status === 403) {
        errorMessage = "You don't have permission to perform this action.";
      } else if (response.status === 404) {
        errorMessage = "Resource not found.";
      } else if (response.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }
    } catch (parseError) {
      errorMessage = response.statusText || errorMessage;
    }

    const err = new Error(errorMessage);
    err.status = response.status;
    err.data = errorData;
    throw err;
  }

  try {
    const data = await response.json();
    return data;
  } catch (parseError) {
    throw new Error("Failed to parse server response");
  }
};

const withTimeout = (promise, timeout = 10000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timeout")), timeout)
    ),
  ]);
};

const withRetry = async (requestFn, maxRetries = 2) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      // Don't retry client errors (4xx) or timeouts
      if (
        attempt === maxRetries ||
        error.message.includes("timeout") ||
        (error.status && error.status >= 400 && error.status < 500)
      ) {
        throw error;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }
};

export const get = async (endpoint, options = {}) => {
  const requestFn = async () => {
    const response = await fetch(
      `${API_BASE_URL}/${endpoint.replace(/^\/+/, "")}`,
      {
        method: "GET",
        headers: getHeaders(),
        ...options,
      }
    );
    return handleResponse(response);
  };

  return withTimeout(withRetry(requestFn));
};

export const post = async (endpoint, data, options = {}) => {
  const isFormData = data instanceof FormData;
  const requestFn = async () => {
    const headers = { ...getHeaders(), ...options.headers };
    if (isFormData) {
      delete headers["Content-Type"];
    }
    const response = await fetch(
      `${API_BASE_URL}/${endpoint.replace(/^\/+/, "")}`,
      {
        method: "POST",
        headers,
        body: isFormData ? data : JSON.stringify(data),
        ...options,
      }
    );
    return handleResponse(response);
  };

  return withTimeout(withRetry(requestFn));
};

export const put = async (endpoint, data, options = {}) => {
  const isFormData = data instanceof FormData;
  const requestFn = async () => {
    const headers = { ...getHeaders(), ...options.headers };
    if (isFormData) {
      delete headers["Content-Type"];
    }
    const response = await fetch(
      `${API_BASE_URL}/${endpoint.replace(/^\/+/, "")}`,
      {
        method: "PUT",
        headers,
        body: isFormData ? data : JSON.stringify(data),
        ...options,
      }
    );
    return handleResponse(response);
  };

  return withTimeout(withRetry(requestFn));
};

export const del = async (endpoint, options = {}) => {
  const requestFn = async () => {
    const response = await fetch(
      `${API_BASE_URL}/${endpoint.replace(/^\/+/, "")}`,
      {
        method: "DELETE",
        headers: getHeaders(),
        ...options,
      }
    );
    return handleResponse(response);
  };

  return withTimeout(withRetry(requestFn));
};

export default {
  setToken,
  removeToken,
  get,
  post,
  put,
  delete: del,
};
